export type OutcomeOrder = 'latest' | 'oldest';

export interface Outcome {
  runId: string;
  sessionId: string;
  sources: Array<{ runId: string; kind: string; actionType: string; title: string }>;
  actionType: string;
  workflow: string;
  icon: string | null;
  title: string;
  summary: string | null;
  kind: string;
  itemCount: number | null;
  creator: string | null;
  completedAt: string;
  accountIds: number[];
  startDate: string;
  endDate: string;
}

export interface OutcomeFilters {
  search: string;
  kind: string | null;
  workflow: string | null;
  creator: string | null;
}

export interface OutcomeScope {
  accountIds: number[];
  from: string;
  to: string;
}

export interface OutcomeSession {
  sessionId: string;
  outcomes: Outcome[];
}

function matches(outcome: Outcome, search: string): boolean {
  const wanted = search.trim().toLowerCase();
  if (wanted === '') return true;
  return [outcome.title, outcome.summary, outcome.workflow, outcome.creator]
    .filter((part) => part !== null)
    .some((part) => part.toLowerCase().includes(wanted));
}

export function shownOutcomes(
  outcomes: Outcome[],
  filters: OutcomeFilters,
  order: OutcomeOrder,
  scope: OutcomeScope | null = null,
): Outcome[] {
  return outcomes
    .filter(
      (outcome) =>
        inScope(outcome, scope) &&
        matches(outcome, filters.search) &&
        (filters.kind === null || outcome.kind === filters.kind) &&
        (filters.workflow === null || outcome.workflow === filters.workflow) &&
        (filters.creator === null || outcome.creator === filters.creator),
    )
    .sort((left, right) =>
      order === 'oldest'
        ? left.completedAt.localeCompare(right.completedAt)
        : right.completedAt.localeCompare(left.completedAt),
    );
}

// No accounts resolved means the scope narrows nothing — the clients query has
// not answered yet. Reading it as "match none" emptied the page while it loaded.
function inScope(outcome: Outcome, scope: OutcomeScope | null): boolean {
  if (scope === null) return true;
  const anyAccount =
    scope.accountIds.length === 0 || outcome.accountIds.some((id) => scope.accountIds.includes(id));
  return anyAccount && outcome.startDate <= scope.to && outcome.endDate >= scope.from;
}

export function groupedBySession(outcomes: Outcome[]): OutcomeSession[] {
  const bySession = new Map<string, Outcome[]>();
  for (const outcome of outcomes) {
    bySession.set(outcome.sessionId, [...(bySession.get(outcome.sessionId) ?? []), outcome]);
  }
  return [...bySession.entries()]
    .map(([sessionId, rows]) => ({ sessionId, outcomes: rows }))
    .sort((left, right) => latestOf(right.outcomes).localeCompare(latestOf(left.outcomes)));
}

function latestOf(outcomes: Outcome[]): string {
  return outcomes.reduce(
    (latest, outcome) => (outcome.completedAt > latest ? outcome.completedAt : latest),
    '',
  );
}

export function choicesOf(outcomes: Outcome[], of: (outcome: Outcome) => string | null): string[] {
  return [...new Set(outcomes.flatMap((outcome) => of(outcome) ?? []))].sort((left, right) =>
    left.localeCompare(right),
  );
}
